import { Divider, Theme } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { camelCase } from 'change-case';
import { TestIds } from '../../../../assets/testIds';
import { SnackbarComponent } from './PartnerEnrollmentCardCommon';
import { getCanonicalPartnerName, PartnerName } from '../../../../common/constants';
import { PartnerSpecificFormData, usePartnerSpecificFormConfigMap } from './PartnerSpecificFormConfig';
import PartnerSpecificForm from './PartnerSpecificForm';
import PartnerNoFormComponent from './PartnerNoFormComponent';
import { useNavigationUtils } from '../../../../common/utils/useNavigationUtils';
import { useFinishPartnerEnrollment } from './useFinishPartnerEnrollment';
import { usePartnerEnrollmentWithExtraStepContext } from './usePartnerEnrollmentWithExtraStep';

function getPartnerSignUpFormBtnId(
  // Canonical (rename-stable) partner name, used only to derive the test-id key.
  partnerKeyName: string,
  buttonType: 'submitBtn' | 'closeBtn',
) {
  return (
    (TestIds.rewardsPage as unknown as {
      [key: string]: {
        signUpForm: { submitBtn: string; closeBtn: string };
      };
    })[camelCase(partnerKeyName)].signUpForm[buttonType]
  );
}

interface PartnerSignUpFormDialogProps {
  updateConfirmationDialogOpenState?: (event: boolean) => void;
  updateEnrollmentDialogOpenState?: (event: boolean) => void;
  partnerName: PartnerName;
  partnerCode: string;
  manufacturerName?: PartnerName;
  manufacturerCode?: string;
}

export function PartnerSignUpFormDialog({
  updateConfirmationDialogOpenState,
  updateEnrollmentDialogOpenState,
  partnerName,
  partnerCode,
  manufacturerName,
  manufacturerCode,
}: PartnerSignUpFormDialogProps) {
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [loading, setLoading] = useState(false);
  const partnerSpecificFormConfigMap = usePartnerSpecificFormConfigMap();
  const partnerConfig = useMemo(() => partnerSpecificFormConfigMap[partnerCode], [
    partnerCode, partnerSpecificFormConfigMap,
  ]);
  // Rename-stable name for deriving the test-id key (see getCanonicalPartnerName).
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);
  const { setEnrollmentFormData, setEnrollmentExtraStepDoneState } =
    usePartnerEnrollmentWithExtraStepContext();
  const { openExternalLink } = useNavigationUtils();
  const finishEnrollment = useFinishPartnerEnrollment({
    updateConfirmationDialogOpenState,
    updateEnrollmentDialogOpenState,
    partnerName,
    partnerCode,
    manufacturerName,
    manufacturerCode,
    setErrorMessage,
    setSnackbarOpen,
    setLoading,
  });
  const handleSnackbarClose = () => {
    setErrorMessage('');
    setSnackbarOpen(false);
  };

  const onSubmit = (data: PartnerSpecificFormData | null) => {
    // steps to do if the partner configuration requires extra enrollment steps
    if (partnerConfig?.hasExtraEnrollmentSteps && partnerConfig?.redirectPath) {
      openExternalLink(partnerConfig?.redirectPath);
      // Key the extra-step state by the stable partner code so it stays in sync
      // with the read side in PartnerEnrollmentDialogContent across a rename.
      setEnrollmentFormData(partnerCode, data);
      setEnrollmentExtraStepDoneState(partnerCode, true);
    }

    // OSP-2506 No matter there is extra step or not,
    // complete the enrolment to confirm user intent to enroll
    finishEnrollment(data);
  };

  return (
    <>
      <Divider sx={(theme: Theme) => ({
        mb: theme.spacing(3),
      })} />
      {partnerConfig ? <PartnerSpecificForm
        config={partnerConfig}
        onSubmitForm={onSubmit}
        loading={loading}
        submitBtnTestId={getPartnerSignUpFormBtnId(partnerKeyName, 'submitBtn')}
        closeBtnTestId={getPartnerSignUpFormBtnId(partnerKeyName, 'closeBtn')}
        manufacturerName={manufacturerName}
      /> : <PartnerNoFormComponent
        partnerName={partnerName}
        partnerCode={partnerCode}
        onSubmitForm={onSubmit}
        loading={loading}
        manufacturerName={manufacturerName}
      />
      }
      <SnackbarComponent
        snackbarOpen={snackbarOpen}
        errorMessage={errorMessage}
        handleSnackbarClose={handleSnackbarClose}
      />
    </>
  );
}
